import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import adminConsoleService, {
  type BasicField,
  type ConfigValueDetail,
  type ConfigValueItem,
} from '@services/adminConsoleService';
import { CustomDetailsTab } from './tabs/CustomDetailsTab';

// ─── Available icons ──────────────────────────────────────────────────────────

export const MENU_ICONS = [
  'icons-menu-tasks',
  'icons-menu-tasks-outline',
  'icons-menu-check-box',
  'icons-menu-check-circle',
  'icons-menu-assignment',
  'icons-menu-assignment-turned',
  'icons-menu-description',
  'icons-menu-insert-drive-file',
  'icons-menu-folder',
  'icons-menu-folder-shared',
  'icons-menu-folder-special',
  'icons-menu-library-books',
  'icons-menu-book',
  'icons-menu-list',
  'icons-menu-view-agenda',
  'icons-menu-view-module',
  'icons-menu-people',
  'icons-menu-person',
  'icons-menu-person-add',
  'icons-menu-account-box',
  'icons-menu-chat',
  'icons-menu-question-answer',
  'icons-menu-announcement',
  'icons-menu-notifications',
  'icons-menu-inbox',
  'icons-menu-move-to-inbox',
  'icons-menu-favorite',
  'icons-menu-grade',
  'icons-menu-star',
  'icons-menu-local-offer',
  'icons-menu-attachment',
  'icons-menu-collections',
  'icons-menu-collections-bookmark',
  'icons-menu-wallpaper',
  'icons-menu-picture-as-pdf',
  'icons-menu-print',
  'icons-menu-create',
  'icons-menu-event-note',
  'icons-menu-dashboard',
  'icons-menu-projects',
  'icons-menu-work',
  'icons-menu-company',
  'icons-menu-users',
  'icons-menu-settings-system-daydream',
  'icons-menu-extension',
  'icons-menu-lightbulb-outline',
  'icons-menu-incandescent',
  'icons-menu-screen-share',
  'icons-menu-newsfeed',
  'icons-menu-all-inclusive',
  'icons-menu-deliverables',
  'icons-menu-epic',
  'icons-menu-designs',
  'icons-menu-help',
  'icons-menu-error',
  'icons-menu-no-sim',
  'icons-menu-menu',
  'icons-menu-close',
  'icons-menu-watch-later',
  'icon2-Tickets',
  'icon2-Questions',
  'icon2-Bugs',
];

// ─── Icon Picker popup ────────────────────────────────────────────────────────

interface IconPickerProps {
  anchorRef: React.RefObject<HTMLDivElement | null>;
  currentIcon: string;
  onSelect: (icon: string) => void;
  onClose: () => void;
}

export const IconPicker: React.FC<IconPickerProps> = ({
  anchorRef,
  currentIcon,
  onSelect,
  onClose,
}) =>
{
  const pos = (() =>
  {
    if (!anchorRef.current) return { bottom: 0, right: 0 };
    const r = anchorRef.current.getBoundingClientRect();
    return { bottom: window.innerHeight - r.top + 6, right: window.innerWidth - r.right };
  })();

  return createPortal(
    <div className="admin-console-portal fixed inset-0 z-[10010]" onClick={onClose}>
      <div
        className="fixed bg-white dark:bg-[#2d2f2f] border border-[#ddd] rounded shadow-xl p-3 z-[10011] leading-none"
        style={{ bottom: pos.bottom, right: pos.right, width: 260 }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between mb-2">
          <p className="text-xs text-[#414042] dark:text-[#a9a093] mx-0 my-1 px-1 py-0">
            Select an icon
          </p>
          <button onClick={onClose} className="text-[#bbb] hover:text-[#666]">
            <span className="material-icons text-[16px]">close</span>
          </button>
        </div>
        <div className="grid grid-cols-7 gap-0 max-h-[150px] overflow-y-auto overflow-x-hidden overflow-scroll border rounded-none border-solid border-[#eee]">
          {MENU_ICONS.map(icon => (
            <button
              key={icon}
              title={icon.replace('icons-menu-', '')}
              onClick={() =>
              {
                onSelect(icon);
                onClose();
              }}
              className={`w-[30px] h-[30px] flex items-center justify-center rounded transition-colors
                ${
            icon === currentIcon
              ? 'bg-[#62aacb] text-white'
              : 'hover:bg-[#f0f0f0] text-[#444]'
            }`}
            >
              <i className={`${icon} text-[18px]`} />
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ─── Edit Card Types Modal ────────────────────────────────────────────────────

export interface EditCardTypesModalProps {
  onClose: () => void;
  onSuccess: (updated: ConfigValueItem[]) => void;
}

export const EditCardTypesModal: React.FC<EditCardTypesModalProps> = ({ onClose, onSuccess }) =>
{
  const [items, setItems] = useState<ConfigValueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() =>
  {
    adminConsoleService
      .getConfigValueAllData()
      .then(setItems)
      .finally(() => setLoading(false));
  }, []);

  const toggleItem = (configValueId: number) =>
  {
    setItems(prev =>
      prev.map(it =>
        it.ConfigValueID === configValueId ? { ...it, IsEnabled: !it.IsEnabled } : it,
      ),
    );
  };

  const filtered = keyword.trim()
    ? items.filter(it => it.SideMenuText.toLowerCase().includes(keyword.trim().toLowerCase()))
    : items;

  const handleSave = async () =>
  {
    setSaving(true);
    try
    {
      const updated = await adminConsoleService.updateCardTypes(items);
      window.dispatchEvent(new CustomEvent('C2_cardTypes_changed'));
      onSuccess(updated);
      onClose();
    }
    finally
    {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="admin-console-portal fixed inset-0 z-[10000] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-[#2d2f2f] rounded shadow-2xl w-[500px] max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-[#e8e8e8]">
          <h2 className="text-[18px] font-semibold text-[#555]">Card Types</h2>
          <button onClick={onClose} className="text-[#aaa] hover:text-[#555]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        {/* Search */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-[#e8e8e8]">
          <label className="text-[13px] text-[#555] whitespace-nowrap">Find by keyword:</label>
          <input
            type="text"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            className="flex-1 border border-[#ccc] rounded px-2 py-1 text-[13px] focus:outline-none focus:border-[#62aacb]"
          />
        </div>

        {/* Table header */}
        <div className="px-5 py-1.5 border-b border-[#e8e8e8] bg-[#f5f5f5]">
          <span className="text-[12px] font-semibold text-[#555]">Name</span>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <span className="material-icons animate-spin text-[#62aacb] text-[28px]">
                refresh
              </span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex items-center justify-center h-20 text-[13px] text-[#aaa]">
              No results.
            </div>
          ) : (
            filtered.map(item => (
              <div
                key={item.ConfigValueID}
                className="flex items-center gap-3 px-5 py-2 border-b border-[#f0f0f0] hover:bg-[#f9f9f9]"
              >
                <span className="corgee_com_checkbox flex-shrink-0">
                  <input
                    id={`ect-${item.ConfigValueID}`}
                    type="checkbox"
                    checked={item.IsEnabled}
                    onChange={() => toggleItem(item.ConfigValueID)}
                  />
                  <label htmlFor={`ect-${item.ConfigValueID}`}></label>
                </span>
                <i className={`${item.Icon} text-[18px] text-[#555]`} />
                <label
                  htmlFor={`ect-${item.ConfigValueID}`}
                  className="text-[13px] text-[#333] cursor-pointer flex-1"
                >
                  {item.SideMenuText}
                </label>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#e8e8e8]">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-[13px] border border-[#ccc] dark:border-[#4a4c4c] rounded text-[#555] dark:text-[#a9a093] hover:bg-[#f5f5f5]"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 text-[13px] rounded text-white bg-[#62aacb] hover:bg-[#4e99ba] disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ─── Add Card Type Modal ──────────────────────────────────────────────────────

interface ModalCardType extends ConfigValueItem {
  selected: boolean;
  hasChanged: boolean;
  isOtherActivity: boolean;
  Text: string;
}

const DEFAULT_NEW_ICON = 'icons-menu-all-inclusive';

export interface AddCardTypeModalProps {
  onClose: () => void;
  onSuccess: (updated: ConfigValueItem[]) => void;
}

export const AddCardTypeModal: React.FC<AddCardTypeModalProps> = ({ onClose, onSuccess }) =>
{
  const [items, setItems] = useState<ModalCardType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [newName, setNewName] = useState('');
  const [newIcon, setNewIcon] = useState(DEFAULT_NEW_ICON);
  const [newIconGrid, setNewIconGrid] = useState<any>();
  const [showNewIconPicker, setShowNewIconPicker] = useState(false);
  const [showNewIconPickerGrid, setShowNewIconPickerGrid] = useState(false);
  const newIconRef = useRef<HTMLDivElement>(null);
  // const newIconGridRef = useRef<HTMLDivElement>(null);
  const anchorElRef = useRef<HTMLDivElement>(null);

  // Inline rename state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() =>
  {
    adminConsoleService
      .getConfigValueAllData()
      .then(data =>
      {
        setItems(
          data.map(ct => ({
            ...ct,
            selected: ct.Selected,
            hasChanged: false,
            isOtherActivity: true,
            Text: ct.Value,
          })),
        );
      })
      .finally(() => setLoading(false));
  }, []);

  const toggleItem = (configValueId: number) =>
  {
    setItems(prev =>
      prev.map(it =>
        it.ConfigValueID === configValueId
          ? { ...it, selected: !it.selected, hasChanged: true }
          : it,
      ),
    );
  };

  // Auto-focus the rename input when it appears
  useEffect(() =>
  {
    if (editingId !== null) editInputRef.current?.select();
  }, [editingId]);

  const startRename = (item: ModalCardType) =>
  {
    setEditingId(item.ConfigValueID);
    setEditingText(item.SideMenuText);
  };

  const commitRename = () =>
  {
    if (editingId === null) return;
    const name = editingText.trim();
    if (name)
    {
      setItems(prev =>
        prev.map(it =>
          it.ConfigValueID === editingId
            ? { ...it, Value: name, SideMenuText: name, Text: name, hasChanged: true }
            : it,
        ),
      );
    }
    setEditingId(null);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (e.key === 'Enter') commitRename();
    if (e.key === 'Escape') setEditingId(null);
  };

  const handleAddNew = async () =>
  {
    const name = newName.trim();
    if (!name) return;
    setAddingItem(true);
    try
    {
      const created = await adminConsoleService.addConfigItem(name, newIcon);
      const newItem: ModalCardType = {
        ...created,
        selected: true,
        hasChanged: true,
        isOtherActivity: true,
        Text: created.Value,
      };
      setItems(prev => [...prev, newItem]);
      setNewName('');
      setNewIcon(DEFAULT_NEW_ICON);
    }
    finally
    {
      setAddingItem(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (e.key === 'Enter') handleAddNew();
  };

  const handleApply = async () =>
  {
    const changed = items
      .filter(it => it.hasChanged)
      .map(x =>
      {
        x.ConfigValueID = x.ConfigValueIDSelected ?? x.ConfigValueID;
        return x;
      });
    if (changed.length === 0)
    {
      onClose();
      return;
    }
    setSaving(true);
    try
    {
      await adminConsoleService.updateCardTypesValues(changed);
      const updatedAll = await adminConsoleService.getConfigValueAll();
      window.dispatchEvent(new CustomEvent('C2_cardTypes_changed'));
      onSuccess(updatedAll);
      onClose();
    }
    finally
    {
      setSaving(false);
    }
  };

  const changeCurrentIcon = (e: React.MouseEvent, item: ModalCardType) =>
  {
    (anchorElRef as React.MutableRefObject<HTMLDivElement | null>).current =
      e.currentTarget as HTMLDivElement;
    setShowNewIconPickerGrid(v => !v);
    setNewIconGrid(item);
  };

  const setItemsGrid = (icon: any, item: ModalCardType) =>
  {
    setItems(prev =>
    {
      return prev.map(x =>
      {
        if (x.ConfigItemID === item.ConfigItemID)
        {
          x.Icon = icon;
          x.hasChanged = true;
        }
        return x;
      });
    });
  };

  return createPortal(
    <div
      className="admin-console-portal fixed inset-0 z-[10000] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
    >
      <div
        className="bg-white dark:bg-[#2d2f2f] rounded shadow-2xl w-[520px] max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <h2 className="text-[18px] font-semibold text-[#62aacb]">Add Card Type</h2>
          <button onClick={onClose} className="text-[#aaa] hover:text-[#555]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        {/* Subtitle */}
        <p className="px-5 text-[13px] text-[#555] mb-2">
          Other than &apos;tasks&apos;, Do you differentiate specific types of activity?
        </p>

        {/* Note banner */}
        <div className="mx-5 mb-3 px-3 py-2 bg-[#e8f4fb] dark:bg-[#1a3a4a] border border-[#b8ddef] dark:border-[#3c6c80] rounded text-[12px] text-[#2e7aac] dark:text-[#62aacb]">
          Note: If you uncheck any custom item type, it will be removed from all related Security
          Groups.
        </div>

        {/* Card types grid (scrollable) */}
        <div className="mx-5 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <span className="material-icons animate-spin text-[#62aacb] text-[28px]">
                refresh
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 pb-2">
              {items.map(item => (
                <div key={item.ConfigValueID} className="flex flex-col items-center gap-1">
                  <div className="relative border border-[#aaa] dark:border-[#4a4c4c] rounded p-3 flex flex-col items-center gap-1 w-full">
                    <span className="corgee_com_checkbox absolute top-1.5 right-1.5">
                      <input
                        id={`act-grid-${item.ConfigValueID}`}
                        type="checkbox"
                        checked={item.selected}
                        onChange={() => toggleItem(item.ConfigValueID)}
                      />
                      <label htmlFor={`act-grid-${item.ConfigValueID}`}></label>
                    </span>
                    <div
                      // ref={newIconGridRef}
                      onClick={e => changeCurrentIcon(e, item)}
                      className="border border-dashed border-[#aaa] dark:border-[#4a4c4c] rounded p-1.5 cursor-pointer hover:border-[#62aacb] flex-shrink-0 leading-none"
                      title="Select icon"
                    >
                      <i className={`${item.Icon} text-[28px] text-[#555]`} />
                    </div>
                  </div>
                  {editingId === item.ConfigValueID ? (
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editingText}
                      onChange={e => setEditingText(e.target.value)}
                      onKeyDown={handleRenameKeyDown}
                      onBlur={commitRename}
                      className="w-full text-[11px] text-center border border-[#62aacb] rounded px-1 py-0.5 focus:outline-none"
                    />
                  ) : (
                    <span
                      className="text-[11px] text-[#444] text-center truncate w-full cursor-text"
                      onDoubleClick={() => startRename(item)}
                      title="Double-click to rename"
                    >
                      {item.SideMenuText}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="mx-5 my-2 border-t border-[#e8e8e8]" />

        {/* Add new row */}
        <div className="px-5 pb-2">
          <p className="text-[12px] text-[#555] mb-2">
            Add a new custom name and press Enter to include it in the list
          </p>
          <div className="flex items-center gap-2">
            <div
              ref={newIconRef}
              onClick={() => setShowNewIconPicker(v => !v)}
              className="border border-dashed border-[#aaa] dark:border-[#4a4c4c] rounded p-1.5 cursor-pointer hover:border-[#62aacb] flex-shrink-0 leading-none"
              title="Select icon"
            >
              <i className={`${newIcon} text-[22px] text-[#555]`} />
            </div>
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={addingItem}
              className="flex-1 border border-[#ccc] rounded px-2 py-1.5 text-[13px] focus:outline-none focus:border-[#62aacb]"
            />
          </div>
        </div>

        {showNewIconPicker && (
          <IconPicker
            anchorRef={newIconRef}
            currentIcon={newIcon}
            onSelect={icon => setNewIcon(icon)}
            onClose={() => setShowNewIconPicker(false)}
          />
        )}

        {showNewIconPickerGrid && (
          <IconPicker
            anchorRef={anchorElRef}
            currentIcon={newIconGrid.Icon}
            onSelect={icon => setItemsGrid(icon, newIconGrid)}
            onClose={() => setShowNewIconPickerGrid(false)}
          />
        )}

        {/* Footer note */}
        <div className="mx-5 mb-3 px-3 py-2 bg-[#e8f4fb] dark:bg-[#1a3a4a] border border-[#b8ddef] dark:border-[#3c6c80] rounded text-[12px] text-[#2e7aac] dark:text-[#62aacb] text-center">
          Add card types to security groups in the admin console to be able to use them.
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-2 px-5 pb-5">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-[13px] border border-[#ccc] dark:border-[#4a4c4c] rounded hover:bg-[#f5f5f5] dark:bg-[#5c6060] dark:hover:brightness-[0.85]
"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={saving}
            className="px-4 py-1.5 text-[13px] rounded text-white bg-[#62aacb] hover:bg-[#4e99ba] disabled:opacity-60 dark:bg-[#3c6c80] dark:hover:brightness-[0.85]"
          >
            {saving ? 'Saving…' : 'Apply Changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

interface AddBasicFieldModalProps {
  open: boolean;
  configValueId: number;
  layerId: number;
  onClose: () => void;
  onSaved: React.Dispatch<React.SetStateAction<ConfigValueDetail | null>>;
}

// ─── Portal wrapper ───────────────────────────────────────────────────────────

const ModalPortal: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  createPortal(<div className="admin-console-portal">{children}</div>, document.body);

// ─── Component ────────────────────────────────────────────────────────────────

export const AddBasicFieldModal: React.FC<AddBasicFieldModalProps> = ({
  open,
  configValueId,
  layerId,
  onClose,
  onSaved,
}) =>
{
  const [fieldsList, setFieldsList] = useState<BasicField[]>([]);
  const [filterValue, setFilterValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCustomDetailsModal, setShowCustomDetailsModal] = useState(false);

  // Focus trap ref
  const dialogRef = useRef<HTMLDivElement>(null);

  // ── Load fields on open ───────────────────────────────────────────────────

  const loadFields = useCallback(async () =>
  {
    setLoading(true);
    try
    {
      const data = await adminConsoleService.getBasicFieldsSP(configValueId, layerId);
      setFieldsList(data);
    }
    finally
    {
      setLoading(false);
    }
  }, [configValueId, layerId]);

  useEffect(() =>
  {
    if (open)
    {
      setFilterValue('');
      loadFields();
    }
  }, [open, loadFields]);

  // ── Keyboard: close on Escape ─────────────────────────────────────────────

  useEffect(() =>
  {
    if (!open || showCustomDetailsModal) return;
    const handleKey = (e: KeyboardEvent) =>
    {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose, showCustomDetailsModal]);

  useEffect(() =>
  {
    if (showCustomDetailsModal) return;
    loadFields();
    const handleKey = (e: KeyboardEvent) =>
    {
      if (e.key === 'Escape') setShowCustomDetailsModal(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [setShowCustomDetailsModal, showCustomDetailsModal]);

  // ── Lock body scroll while open ───────────────────────────────────────────

  useEffect(() =>
  {
    if (open)
    {
      document.body.style.overflow = 'hidden';
    }
    else
    {
      document.body.style.overflow = '';
    }
    return () =>
    {
      document.body.style.overflow = '';
    };
  }, [open]);

  // ── Checkbox toggle ───────────────────────────────────────────────────────

  const toggleField = (id: number) =>
  {
    setFieldsList(prev =>
      prev.map(f =>
        f.BasicFieldId === id ? { ...f, IsUsedInLayer: f.IsUsedInLayer ? false : true } : f,
      ),
    );
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () =>
  {
    setSaving(true);
    try
    {
      const data = await adminConsoleService.addBasicFieldsToLayers({
        BasicFields: fieldsList,
        LayerId: layerId,
        ConfigValueId: configValueId,
      });
      onSaved(prev =>
      {
        if (!prev) return prev;
        const index = prev.Layers.findIndex(x => x.LayerId === layerId);
        if (index !== -1) prev.Layers[index]!.CustomFields = data;
        return prev;
      });
      // onSaved(data);
    }
    finally
    {
      onClose();
      setSaving(false);
    }
  };

  // ── Filtered list ─────────────────────────────────────────────────────────

  const filteredFields = filterValue.trim()
    ? fieldsList.filter(f =>
      [f.Name, (f as any).CollectionTypeDescription, (f as any).Options]
        .join(' ')
        .toLowerCase()
        .includes(filterValue.toLowerCase()),
    )
    : fieldsList;

  // ── Render ────────────────────────────────────────────────────────────────

  if (!open) return null;

  const onOpenCustomFieldWizard = () =>
  {
    setShowCustomDetailsModal(true);
  };

  return (
    <>
      <ModalPortal>
        {/* Backdrop */}
        <div
          className="fixed inset-0  flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
          onClick={onClose}
        />

        {/* Dialog */}
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-basic-field-title"
          className="fixed -translate-x-2/4 -translate-y-2/4 w-[660px] max-w-[95vw] max-h-[90vh] flex flex-col bg-white dark:bg-[#2d2f2f] rounded shadow-[rgba(0,0,0,0.2)_0px_11px_15px_-7px,rgba(0,0,0,0.14)_0px_24px_38px_3px,rgba(0,0,0,0.12)_0px_9px_46px_8px] z-[1001] overflow-hidden left-2/4 top-2/4"
        >
          {/* Toolbar */}
          <div className="flex items-center justify-between px-5 pt-5 pb-2">
            <h2 className="text-[18px] font-semibold text-[#62aacb]">Basic Details</h2>
            <button onClick={onClose} className="text-[#aaa] hover:text-[#555]">
              <span className="material-icons text-[18px]">close</span>
            </button>
          </div>

          {/* Content */}
          <div className="px-8 pb-8 overflow-y-auto flex-1">
            {/* Create custom detail link */}
            <div className="flex justify-end mb-4">
              <button
                type="button"
                className="text-[#62aacb] text-[12px] flex items-center gap-1"
                onClick={onOpenCustomFieldWizard}
              >
                <span className="material-icons text-[14px]">add</span>
                <span className="hover:underline">Create Custom Detail</span>
              </button>
            </div>

            {/* Filter */}
            <div className="flex items-center gap-3 px-5 py-3 border-b border-[#e8e8e8]">
              <label className="text-[13px] text-[#555] whitespace-nowrap">Find by keyword:</label>

              <input
                id="basic-field-filter"
                className="flex-1 border border-[#ccc] rounded px-2 py-1 text-[13px] focus:outline-none focus:border-[#62aacb]"
                type="text"
                value={filterValue}
                onChange={e => setFilterValue(e.target.value)}
              />
            </div>

            {/* Table */}
            <div
              id="table-wrapper"
              className="grid add-custom-table h-80 overflow-hidden rounde-[3px] border border-[#e0e0e0]"
            >
              <div id="table-scroll" className="max-h-80 overflow-auto">
                {loading ? (
                  <div className="text-center p-8 text-[#888] text-sm">Loading…</div>
                ) : (
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr>
                        <th style={{ ...styles.th, width: '40px' }}>&nbsp;</th>
                        <th style={{ ...styles.th, paddingLeft: 10 }}>Name</th>
                        <th style={{ ...styles.th, paddingLeft: 10 }}>Description Type</th>
                        <th style={{ ...styles.th, paddingLeft: 10 }}>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFields.length === 0 ? (
                        <tr className="odd:bg-white even:bg-[#f1f1f1]">
                          <td colSpan={4} className="text-center p-6 text-[#999] text-sm">
                            No results found.
                          </td>
                        </tr>
                      ) : (
                        filteredFields.map(row => (
                          <tr key={row.BasicFieldId} className="odd:bg-white even:bg-[#f1f1f1]">
                            <td style={{ ...styles.td, width: '40px', textAlign: 'center' }}>
                              <span className="corgee_com_checkbox">
                                <input
                                  id={`chk${row.BasicFieldId}`}
                                  type="checkbox"
                                  checked={!!row.IsUsedInLayer}
                                  onChange={() => toggleField(row.BasicFieldId)}
                                />
                                <label htmlFor={`chk${row.BasicFieldId}`}></label>
                              </span>
                            </td>
                            <td className="p-2 border-b border-[#eee] align-middle">{row.Name}</td>
                            <td className="p-2 border-b border-[#eee] align-middle">
                              {(row as any).CollectionTypeDescription}
                            </td>
                            <td className="p-2 border-b border-[#eee] align-middle">
                              {(row as any).Options}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 px-5 pb-5">
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-[13px] border border-[#ccc] dark:border-[#4a4c4c] rounded text-[#555] dark:text-[#a9a093] dark:bg-[#5c6060] dark:hover:brightness-[0.85] hover:bg-[#f5f5f5]"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-1.5 text-[13px] rounded text-white bg-[#62aacb] hover:bg-[#4e99ba] dark:bg-[#3c6c80] dark:hover:brightness-[0.85] disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </ModalPortal>

      {showCustomDetailsModal && (
        <ModalPortal>
          <div
            className="fixed inset-0 z-[1010] flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
            onClick={() => setShowCustomDetailsModal(false)}
          />

          {/* Dialog */}
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-basic-field-title"
            className="fixed -translate-x-2/4 -translate-y-2/4 w-[660px] max-w-[95vw] max-h-[60vh] flex flex-col bg-white dark:bg-[#2d2f2f] rounded shadow-[rgba(0,0,0,0.2)_0px_11px_15px_-7px,rgba(0,0,0,0.14)_0px_24px_38px_3px,rgba(0,0,0,0.12)_0px_9px_46px_8px] z-[1011] overflow-hidden left-2/4 top-2/4 overflow-y-auto overflow-x-hidden"
          >
            {/* Toolbar */}
            <div className="flex items-center justify-between px-5 pt-5 pb-2 sticky">
              <h2 className="text-[18px] font-semibold text-[#62aacb]">Basic Details</h2>
              <button
                onClick={() => setShowCustomDetailsModal(false)}
                className="text-[#aaa] hover:text-[#555]"
              >
                <span className="material-icons text-[18px]">close</span>
              </button>
            </div>
            <div className="mx-3 mb-3">
              <CustomDetailsTab prevOverflow={true} />
            </div>
          </div>
        </ModalPortal>
      )}
    </>
  );
};

const styles = {
  linkButton: {
    background: 'none',
    border: 'none',
    color: '#009688',
    cursor: 'pointer',
    fontSize: 14,
    padding: 0,
    textDecoration: 'underline',
  },
  th: {
    position: 'sticky' as const,
    top: 0,
    backgroundColor: '#f5f5f5',
    fontWeight: 600 as const,
    padding: '10px 8px',
    borderBottom: '2px solid #ddd',
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
    zIndex: 1,
  },
  td: {
    padding: '8px',
    borderBottom: '1px solid #eee',
    verticalAlign: 'middle' as const,
  },
  tr: {
    transition: 'background 0.15s',
  },
} as const;
